import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  MoreOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, Modal, notification, Space, Typography } from "antd";
import {
  IOU_COST_DELETE_ENDPOINT,
  IOU_COST_EXCEL_ENDPOINT,
  IOU_COST_LIST_ENDPOINT,
  IOU_COST_PDF_ENDPOINT,
  IOU_DOCUMENT_GET_ENDPOINT,
  IOU_REQUESTED_BY_USER_ENDPOINT,
} from "apiServices/API_ENDPOINTS";
import { deleteData, getData, postData } from "apiServices/common";
import { alertPop } from "apiServices/common/helper";
import AppContent from "common/AppContent";
import ListTable from "common/ListTable";
import HandleNotification from "common/Notification";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  IOU_CREATE_PERMISSION,
  IOU_DELETE_PERMISSION,
  IOU_EDIT_PERMISSION,
  IOU_VIEW_PERMISSION,
} from "routes/permissions";
import { hasPermission, toFixedAndComma } from "utils/functions";
import { checkPermission } from "./config/checkPermission";
import UploadDocument from "./UploadDocument";

export default function IOUCost() {
  const location = useLocation();
  const navigate = useNavigate();
  const routerPath = location ? location.pathname : null;
  const loggedInUserId = parseInt(localStorage.getItem("user_id"), 10);

  const loggedInUserRole = localStorage.getItem("user_type");

  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [canRequest, setCanRequest] = useState(false);
  const [canApprove, setCanApprove] = useState(false);
  const [canReject, setCanReject] = useState(false);
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [filesList, setFileList] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const [exportLoading, setExportLoading] = useState(false);

  const { Title } = Typography;

  const normalizeDateValue = useCallback((dateValue) => {
    if (!dateValue) {
      return null;
    }

    if (typeof dateValue === "string") {
      return dateValue;
    }

    if (typeof dateValue?.format === "function") {
      return dateValue.format("YYYY-MM-DD");
    }

    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return null;
    }

    return parsedDate.toISOString().split("T")[0];
  }, []);

  const normalizeFilterValues = useCallback(
    (filterValues = {}) => ({
      iou_number: filterValues?.iou_number || null,
      status: filterValues?.status || null,
      iou_date: normalizeDateValue(filterValues?.iou_date),
      taken_by: filterValues?.taken_by || null,
      from_date: normalizeDateValue(filterValues?.from_date),
      to_date: normalizeDateValue(filterValues?.to_date),
    }),
    [normalizeDateValue],
  );

  const handleFilterChange = useCallback(
    (filterValues) => {
      setCurrentPage(1);
      setActiveFilters(normalizeFilterValues(filterValues));
    },
    [normalizeFilterValues],
  );

  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      localStorage.setItem(
        "filter_status",
        JSON.stringify({
          ...parsedFilterStatus,
          [routerPath]: true,
        }),
      );
    } else {
      localStorage.setItem(
        "filter_status",
        JSON.stringify({
          ...parsedFilterStatus,
          [routerPath]: false,
        }),
      );
    }
  }, [showFilter, routerPath]);

  const getAllIOUCosts = useCallback(
    async (filterValues) => {
      const normalizedFilters = normalizeFilterValues(filterValues);
      const { iou_number, status, iou_date, taken_by, to_date, from_date } = normalizedFilters;

      setLoading(true);
      setFetchError(null);

      const query = `${IOU_COST_LIST_ENDPOINT}?page=${currentPage}`;
      const bodyData = {
        ...(iou_number && { iou_number }),
        ...(status && { status }),
        ...(iou_date && { iou_date }),
        ...(taken_by && { taken_by }),
        ...(to_date && { to_date }),
        ...(from_date && { from_date }),
      };

      const response = await getData(query, false, bodyData);
      if (response?.status === 200) {
        const data = response.data?.data?.data || [];

        const dataWithKeys = data.map((item, index) => ({
          ...item,
          key: item.id || `iou-cost-${index}`,
        }));

        setTableData(dataWithKeys);
        setMetaData({
          current: response.data?.data?.current_page || 1,
          defaultCurrent: response?.data?.data?.from || 1,
          defaultPageSize: response?.data?.data?.per_page || 10,
          total: response.data?.data?.total || 0,
        });
        setLoading(false);
      } else {
        setFetchError("Could not load this list.");
        setLoading(false);
      }
    },

    [currentPage, normalizeFilterValues],
  );

  useEffect(() => {
    getAllIOUCosts(activeFilters);
  }, [refresh, activeFilters, getAllIOUCosts]);

  const refetch = useCallback(() => {
    setRefresh((prev) => prev + 1);
  }, []);

  const deleteSelectedIOUCost = useCallback(
    async (id) => {
      Modal.confirm({
        title: "Confirm Deletion",
        content: "Are you sure you want to delete this IOU Cost?",
        okText: "Yes, Delete",
        okType: "danger",
        cancelText: "Cancel",
        onOk: async () => {
          const response = await deleteData(`${IOU_COST_DELETE_ENDPOINT}/${id}`);

          if (response && (response?.code === 200 || response?.status === 200)) {
            setLoading(false);
            notification.success({
              message: "Success",
              description: "IOU Cost deleted successfully",
              placement: "topRight",
            });
            refetch();
          }
        },
        onCancel: () => {
          setLoading(false);
        },
      });
    },
    [refetch],
  );

  const navigateToAdd = useCallback(() => {
    navigate("/import-export-expense-tracking/iou-cost/add");
  }, [navigate]);

  const navigateToEdit = useCallback(
    (record) => {
      navigate(`/import-export-expense-tracking/iou-cost/edit/${record.id}`);
    },
    [navigate],
  );

  const navigateToView = useCallback(
    (record) => {
      navigate(`/import-export-expense-tracking/iou-cost/view/${record.id}`);
    },
    [navigate],
  );

  const changePage = (newPage) => {
    setCurrentPage(newPage);
  };

  const onSelectChange = (newSelectedRowKeys, newSelectedRows) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRows(newSelectedRows);

    const allSameStatus =
      newSelectedRows.length > 0 && newSelectedRows.every((row) => row.status === newSelectedRows[0].status);

    if (allSameStatus) {
      const canRequestStatus = newSelectedRows.some((row) =>
        checkPermission(
          loggedInUserRole,
          row.status,
          loggedInUserId === row?.user?.id,
          "request",
          row?.department_approve,
          row?.director_approve,
          row?.account_approve,
        ),
      );

      const canApproveStatus = newSelectedRows.some((row) =>
        checkPermission(
          loggedInUserRole,
          row.status,
          loggedInUserId === row?.user?.id,
          "approve",
          row?.department_approve,
          row?.director_approve,
          row?.account_approve,
        ),
      );

      const canRejectStatus = newSelectedRows.some((row) =>
        checkPermission(
          loggedInUserRole,
          row.status,
          loggedInUserId === row?.user?.id,
          "reject",
          row?.department_approve,
          row?.director_approve,
          row?.account_approve,
        ),
      );

      setCanRequest(canRequestStatus);
      setCanApprove(canApproveStatus);
      setCanReject(canRejectStatus);
    } else {
      setCanRequest(false);
      setCanApprove(false);
      setCanReject(false);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record) => ({
      // disabled: !["created", "requested", "by_department", "rejected", "by_director"].includes(record.status),
    }),
  };

  const handleRequestAction = async (val) => {
    if (selectedRows.length === 0) {
      notification.warning({
        message: "Warning",
        description: "No rows selected for the request",
        placement: "bottomRight",
      });
      return;
    }

    Modal.confirm({
      title: "Confirm Request",
      content: "Are you sure you want to submit the selected IOU Costs?",
      okText: "Yes, Submit",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setLoading(true);
          const requestedIds = selectedRows.map((row) => row.id);
          const formData = new FormData();
          formData.append("requested_id", JSON.stringify(requestedIds));
          formData.append("status", val);

          const response = await postData(IOU_REQUESTED_BY_USER_ENDPOINT, formData);

          if (response && (response?.code === 200 || response?.status === 200)) {
            alertPop("success", response.message || "Request submitted successfully");

            setSelectedRowKeys([]);
            setSelectedRows([]);

            refetch();
          }
        } catch (error) {
          notification.error({
            message: "Error",
            description: "Failed to submit IOU Cost form",
            placement: "bottomRight",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const tableColumns = useMemo(
    () => [
      {
        title: "IOU Number",
        dataIndex: "iou_number",
        width: "15%",
        key: "iou_number",
      },
      {
        title: "IOU For",
        dataIndex: "iou_for",
        width: "15%",
        key: "iou_for",
      },
      {
        title: "Amount",
        dataIndex: "iou_amount",
        width: "15%",
        key: "iou_amount",
        render: (text) => toFixedAndComma(text || 0),
      },
      {
        title: "Requested By",
        dataIndex: ["user", "name"],
        width: "15%",
        key: "taken_by",
      },

      {
        title: "Date",
        dataIndex: "iou_date",
        width: "15%",
        key: "iou_date",
      },
      {
        title: "Status",
        dataIndex: "status",
        width: "15%",
        key: "status",

        render: (status) => {
          let color = "blue";
          let text = status || "N/A";

          if (status === "requested") {
            color = "orange";
            text = "Requested";
          } else if (status === "approved") {
            color = "green";
            text = "Approved";
          } else if (status === "rejected") {
            color = "red";
            text = "Rejected";
          } else if (status === "by_department") {
            color = "purple";
            text = "By Department";
          } else if (status === "by_director") {
            color = "gold";
            text = "By Director";
          }

          return (
            <span
              style={{
                color: "#fff",
                backgroundColor: color,
                padding: "4px 8px",
                borderRadius: "4px",
                textTransform: "capitalize",
              }}
            >
              {text}
            </span>
          );
        },
      },
      {
        title: "Action",
        key: "action",
        width: "10%",
        render: (_, record) => {
          const isCreator = loggedInUserId === record?.user?.id;

          const canView = checkPermission(
            loggedInUserRole,
            record.status,
            isCreator,
            "view",
            "update",
            record?.department_approve,
            record?.director_approve,
            record?.account_approve,
          );

          const canUpdate = checkPermission(
            loggedInUserRole,
            record.status,
            isCreator,
            "update",
            record?.department_approve,
            record?.director_approve,
            record?.account_approve,
          );

          const canReject = checkPermission(loggedInUserRole, record.status, isCreator, "reject");

          const canRequest = checkPermission(loggedInUserRole, record.status, isCreator, "request");

          const menuItems = [
            ...(canView
              ? [
                  {
                    key: "view",
                    label: "View",
                    icon: <EyeOutlined />,
                    onClick: () => navigateToView(record),
                    disabled: !hasPermission([IOU_VIEW_PERMISSION]),
                  },
                ]
              : []),
            ...(canUpdate
              ? [
                  {
                    key: "edit",
                    label: "Edit",
                    icon: <EditOutlined />,
                    onClick: () => navigateToEdit(record),
                    disabled: !hasPermission([IOU_EDIT_PERMISSION]),
                  },
                ]
              : []),
            ...(record.status === "created"
              ? [
                  {
                    type: "divider",
                  },
                  {
                    key: "delete",
                    label: "Delete",
                    icon: <DeleteOutlined />,
                    onClick: () => deleteSelectedIOUCost(record.id),
                    disabled: !hasPermission([IOU_DELETE_PERMISSION]),
                    danger: true,
                  },
                ]
              : []),
            {
              key: "Download",
              label: "Download",
              icon: <FilePdfOutlined />,
              onClick: () => handleDownloadAction(record),
              disabled: !hasPermission([IOU_VIEW_PERMISSION]),
            },
            // ...(record?.disburse_status === "approved"
            //   ? [
            //       {
            //         key: "upload",
            //         label: "Upload",
            //         icon: <UploadOutlined />,
            //         onClick: () => openUploadModal(record),
            //         disabled: !hasPermission([IOU_VIEW_PERMISSION]),
            //       },
            //     ]
            //   : []),
          ];

          return (
            <Dropdown menu={{ items: menuItems }} placement="bottomRight" trigger={["hover"]}>
              <Button type="default" icon={<MoreOutlined />} />
            </Dropdown>
          );
        },
      },
    ],
    [deleteSelectedIOUCost, navigateToEdit, navigateToView, loggedInUserId, loggedInUserRole],
  );

  const openUploadModal = async (record) => {
    setSelectedRecord(record);
    setIsUploadModalVisible(true);
    const params = {
      iou_id: record.id,
    };

    try {
      const response = await getData(`${IOU_DOCUMENT_GET_ENDPOINT}`, false, params);
      if (response?.status === 200) {
        const documents = response?.data?.data || [];
        const formattedDocuments = documents.map((doc) => ({
          id: doc.id,
          name: doc.document_name,
          path: doc.document,
        }));
        setFileList(formattedDocuments);
      } else {
        notification.error({
          message: "Error",
          description: "Failed to fetch documents",
          placement: "topRight",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "An error occurred while fetching documents",
        placement: "topRight",
      });
    }
  };

  // Function to handle closing the modal
  const closeUploadModal = () => {
    setIsUploadModalVisible(false);
    setSelectedRecord(null);
  };

  const handleDownloadAction = async (record) => {
    try {
      setLoading(true);

      const response = await getData(
        `${IOU_COST_PDF_ENDPOINT}/${record.id}`,
        false,
        {},
        { "Content-Type": "application/pdf" },
        "blob",
      );

      if (response?.status === 200) {
        const outputFilename = `iou_report_${record.iou_number}.pdf`;

        const url = URL.createObjectURL(
          new Blob([response?.data], {
            type: "application/pdf",
          }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", outputFilename);
        document.body.appendChild(link);
        link.click();
      } else {
        HandleNotification("error", "bottomRight", "Error getting PDF report!", null);
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to disburse IOU",
        placement: "topRight",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    setExportLoading(true);

    try {
      const { iou_number, status, iou_date, taken_by, to_date, from_date } =
        normalizeFilterValues(activeFilters);

      const bodyData = {
        ...(iou_number && { iou_number }),
        ...(status && { status }),
        ...(iou_date && { iou_date }),
        ...(taken_by && { taken_by }),
        ...(to_date && { to_date }),
        ...(from_date && { from_date }),
      };

      const response = await getData(
        IOU_COST_EXCEL_ENDPOINT,
        false,
        bodyData,
        { "Content-Type": "blob" },
        "arraybuffer",
      );

      if (response?.data) {
        const outputFilename = `iou_cost_${new Date().toISOString().slice(0, 10)}.xlsx`;
        const url = URL.createObjectURL(
          new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", outputFilename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        notification.success({
          message: "Success",
          description: "IOU Cost Excel downloaded successfully",
          placement: "topRight",
        });
      } else {
        notification.error({
          message: "Error",
          description: "Failed to download IOU Cost Excel",
          placement: "topRight",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to download IOU Cost Excel",
        placement: "topRight",
      });
    } finally {
      setExportLoading(false);
    }
  };

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={handleFilterChange}
        filterName="iou_cost"
        showFilter={showFilter}
        setShowFilter={setShowFilter}
      />
    ),
    [showFilter],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <div style={{ display: "flex", alignItems: "center" }}>
                {selectedRowKeys.length > 0 && canReject && (
                  <Button
                    type="danger"
                    icon={<CloseOutlined style={{ color: "white" }} />}
                    onClick={() => handleRequestAction("rejected")}
                    style={{
                      backgroundColor: "#ff4d4f",
                      borderColor: "#ff4d4f",
                      color: "white",
                      padding: "0 8px",
                      marginRight: 10,
                    }}
                  >
                    Reject
                  </Button>
                )}{" "}
                {selectedRowKeys.length > 0 && canRequest && (
                  <Button
                    type="success"
                    icon={<CheckOutlined style={{ color: "white" }} />}
                    onClick={() => handleRequestAction("requested")}
                    style={{
                      backgroundColor: "#52c41a",
                      borderColor: "#52c41a",
                      color: "white",
                      padding: "0 8px",
                      marginRight: 10,
                    }}
                  >
                    Request
                  </Button>
                )}
                {selectedRowKeys.length > 0 && canApprove && (
                  <Button
                    type="success"
                    icon={<CheckOutlined style={{ color: "white" }} />}
                    onClick={() => handleRequestAction("approved")}
                    style={{
                      backgroundColor: "#52c41a",
                      borderColor: "#52c41a",
                      color: "white",
                      padding: "0 8px",
                      marginRight: 10,
                    }}
                  >
                    Approve
                  </Button>
                )}
                <Space>
                  <Button
                    type="primary"
                    size="small"
                    icon={<FileExcelOutlined />}
                    loading={exportLoading}
                    onClick={handleExportExcel}
                    disabled={!hasPermission([IOU_VIEW_PERMISSION])}
                  >
                    Download Excel
                  </Button>
                  <ExtraContent
                    refresh={refresh}
                    setRefresh={setRefresh}
                    handleAddItemOpen={navigateToAdd}
                    disabled={!hasPermission([IOU_CREATE_PERMISSION])}
                    addButtonText={hasPermission([IOU_CREATE_PERMISSION]) ? "Add New" : "No Permission"}
                    showFilter={showFilter}
                    setShowFilter={setShowFilter}
                  />
                </Space>
              </div>
            }
            title={<Title level={5}>IOU Cost</Title>}
          />
        }
        content={
          <>
            {memoizedCustomFilter}

            <div className="table" style={{ marginTop: 2 }}>
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => getAllIOUCosts(activeFilters)}
                dataSource={tableData}
                columns={tableColumns}
                rowSelection={rowSelection}
                rowKey="id"
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize || 10,
                  current: metaData?.current || 1,
                  onChange: changePage,
                  defaultCurrent: metaData?.defaultCurrent || 1,
                  total: metaData?.total || 0,
                  showTotal: (total) => `Total ${total} items`,
                }}
                bordered
                size="middle"
                scroll={{
                  x: "max-content",
                  y: tableData.length > 10 ? 500 : undefined,
                }}
                style={{ borderRadius: "8px", overflow: "hidden" }}
              />
            </div>

            <Modal
              title="Upload Document"
              visible={isUploadModalVisible}
              onCancel={closeUploadModal}
              footer={null}
              width={1000}
            >
              <UploadDocument record={selectedRecord} setFileList={setFileList} filesList={filesList} />
            </Modal>
          </>
        }
      />
    </>
  );
}
